<template>
  <div class="inactive-student-report">
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('inactiveStudent.pageTitle') }}</h1>
      <ts-breadcrumb :items="[
        { text: $t('toHomePage'), href: '/' },
        {
          text: $t('inactiveStudent.pageTitle'),
          active: true
        }
      ]" />
    </div>
    <div class="row mt-3">
      <div class="col-12">
        <ts-loading-banner :loading="loading">
          <form class="filter-form">
            <div class="criteria-panel">
              <div class="criteria-header">
                <span class="criteria-icon"><i class="fa fa-sliders"></i></span>
                <span class="criteria-title">{{ $t('inactiveStudent.criteria') }}</span>
              </div>
              <div class="criteria-body">
                <div class="row form-grid">
                  <div class="col-12 col-md-6 col-xl form-control-wrap">
                    <div class="form-group mb-0">
                      <label class="required">{{ $t('inactiveStudent.branchName') }}</label>
                      <a-select style="width:100%" v-model.number="model.branch_id" :options="branches"></a-select>
                      <div class="validate" v-if="errors.has('branch_id')">
                        {{ errors.first('branch_id') }}
                      </div>
                    </div>
                  </div>

                  <div class="col-12 col-md-6 col-xl form-control-wrap">
                    <div class="form-group mb-0">
                      <label class="required">{{ $t('inactiveStudent.dateRange') }}</label>
                      <a-range-picker class="w-100" format="DD-MM-YYYY" :defaultValue="rangeDate"
                        :defaultPickerValue="rangeDate" @change="dateRangeChange">
                        <template slot="renderExtraFooter"></template>
                      </a-range-picker>
                    </div>
                  </div>

                  <div class="col-12 col-md-6 col-xl form-control-wrap">
                    <div class="form-group mb-0">
                      <label>{{ $t('inactiveStudent.mainProgram') }}</label>
                      <a-select style="width:100%" mode="multiple" :placeholder="$t('report.all')"
                        v-model="model.acp_id" :options="main_programs" @change="onMainProgramChange"></a-select>
                    </div>
                  </div>

                  <div class="col-12 col-md-6 col-xl form-control-wrap">
                    <div class="form-group mb-0">
                      <label>{{ $t('inactiveStudent.subProgram') }}</label>
                      <a-select style="width:100%" mode="multiple" :placeholder="$t('report.all')"
                        v-model="model.acps_id" :options="sub_programs"></a-select>
                      <div class="validate" v-if="errors.has('acps_id')">
                        {{ errors.first('acps_id') }}
                      </div>
                    </div>
                  </div>

                  <div class="col-12 col-md-6 col-xl form-control-wrap">
                    <div class="form-group mb-0">
                      <label>{{ $t('inactiveStudent.status') }}</label>
                      <a-select style="width:100%" mode="multiple" :placeholder="$t('report.all')"
                        v-model="model.status_id" :options="status.map(i => ({
                          value: i.status_id,
                          label: i.status
                        }))
                          "></a-select>
                    </div>
                  </div>
                </div>

                <div class="action-panel">
                  <div class="action-left">
                    <a-radio-group v-model="model.report_view" button-style="solid" class="report-view-radio">
                      <a-radio-button value="sub_program">
                        {{ $t('inactiveStudent.bySubProgram') }}
                      </a-radio-button>
                      <a-radio-button value="term">
                        {{ $t('inactiveStudent.byTerm') }}
                      </a-radio-button>
                      <a-radio-button value="detail">
                        {{ $t('inactiveStudent.detail') }}
                      </a-radio-button>
                    </a-radio-group>
                  </div>
                  <div class="action-right">
                    <ts-button class="btn-action mr-sm-2" :class="{ 'btn-active': activeView === 'result' }" outline
                      color="info" @click.prevent="loadResults" :waiting="loadingResult">{{
                        $t('inactiveStudent.loadData') }}</ts-button>
                    <ts-button class="btn-action" :class="{ 'btn-active': activeView === 'preview' }" color="primary"
                      @click.prevent="preview" :waiting="waiting">{{ $t('inactiveStudent.previewPdf') }}</ts-button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </ts-loading-banner>
      </div>
      <div class="col-12 mt-3">
        <ts-card>
          <ts-card-wrapper>
            <div class="result-header result-header--flat">
              <div class="d-flex align-items-center">
                <h5 class="mb-0">
                  {{ activeView === 'preview' ? $t('inactiveStudent.previewPdf') : $t('inactiveStudent.result') }}
                </h5>
                <span class="result-tag ml-2">{{ summaryLabel }}</span>
              </div>
              <div v-if="activeView === 'result'" class="result-count">
                {{ records.length }} {{ $t('inactiveStudent.totalStudent') }}
              </div>
            </div>

            <form v-if="activeView === 'preview'" class="result-content">
              <fieldset class="result-fieldset">
                <ts-preview-pdf v-model="waiting" :src="src" />
              </fieldset>
            </form>
            <form v-if="activeView === 'result'" class="result-content">
              <fieldset class="result-fieldset">
                <div class="table-shell" v-if="records.length">
                <div class="table-responsive" v-if="model.report_view === 'detail'">
                  <table class="table table-sm table-bordered table-striped mb-0 result-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{{ $t('inactiveStudent.cardNumber') }}</th>
                        <th>{{ $t('inactiveStudent.studentName') }}</th>
                        <th>{{ $t('inactiveStudent.sex') }}</th>
                        <th>{{ $t('inactiveStudent.phone') }}</th>
                        <th>{{ $t('inactiveStudent.subProgram') }}</th>
                        <th>{{ $t('inactiveStudent.acTerm') }}</th>
                        <th>{{ $t('inactiveStudent.date') }}</th>
                        <th>{{ $t('inactiveStudent.status') }}</th>
                        <th>{{ $t('inactiveStudent.reason') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(record, index) in records" :key="record.code + '-' + index">
                        <td>{{ index + 1 }}</td>
                        <td>{{ record.code }}</td>
                        <td>{{ record.student_name }}</td>
                        <td>{{ record.sex }}</td>
                        <td>{{ record.phone }}</td>
                        <td>{{ record.acps_name_foreign }}</td>
                        <td>{{ getTermName(record) }}</td>
                        <td>{{ record.t_date }}</td>
                        <td>{{ record.status }}</td>
                        <td>{{ record.reason || $t('inactiveStudent.notAvailable') }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="table-responsive" v-else>
                  <table class="table table-sm table-bordered table-striped mb-0 result-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{{ $t('inactiveStudent.mainProgram') }}</th>
                        <th>{{ $t('inactiveStudent.subProgram') }}</th>
                        <th v-if="model.report_view === 'term'">{{ $t('inactiveStudent.acTerm') }}</th>
                        <th>{{ $t('inactiveStudent.totalStudent') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, index) in summaryRows" :key="row.group + '-' + index">
                        <td>{{ index + 1 }}</td>
                        <td>{{ row.mainProgram }}</td>
                        <td>{{ row.subProgram }}</td>
                        <td v-if="model.report_view === 'term'">{{ row.term }}</td>
                        <td>{{ row.total }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                </div>
                <div class="empty-result" v-else>
                  <div class="empty-icon"><a-icon type="inbox" /></div>
                  <div class="empty-title">{{ $t('inactiveStudent.noRecord') }}</div>
                  <div class="empty-subtitle">{{ $t('inactiveStudent.emptyHint') }}</div>
                </div>
              </fieldset>
            </form>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapState } from 'vuex'
import { Errors } from 'form-backend-validation'
import moment from 'moment'

export default {
  name: 'inactiveStudent',
  data() {
    return {
      loading: false,
      waiting: false,
      loadingResult: false,
      src: '',
      records: [],
      activeView: 'result',
      errors: new Errors(),
      branches: [],
      rangeDate: [moment(), moment()],
      main_programs: [],
      sub_programs: [],
      fetching: false,
      model: {
        branch_id: null,
        acp_id: [],
        acps_id: [],
        status_id: [],
        report_view: 'sub_program'
      }
    }
  },
  computed: {
    ...mapState('reportAcademic', ['studentStatus']),
    status() {
      return this.studentStatus.filter(s => s.status_id !== 1)
    },
    summaryLabel() {
      if (this.model.report_view === 'detail') {
        return this.$t('inactiveStudent.detail')
      }

      return this.model.report_view === 'term'
        ? this.$t('inactiveStudent.byTerm')
        : this.$t('inactiveStudent.bySubProgram')
    },
    summaryRows() {
      if (!this.records.length) {
        return []
      }

      const grouped = {}
      this.records.forEach(record => {
        const mainProgram = this.getMainProgramName(record)
        const subProgram = this.getSubProgramName(record)
        const term = this.getTermName(record)
        const groupName = this.model.report_view === 'term'
          ? `${mainProgram}__${subProgram}__${term}`
          : `${mainProgram}__${subProgram}`

        if (!grouped[groupName]) {
          grouped[groupName] = {
            group: groupName,
            mainProgram,
            subProgram,
            term,
            total: 0
          }
        }
        grouped[groupName].total += 1
      })

      return Object.values(grouped)
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    ...mapActions('reportAcademic', [
      'getBranch',
      'getMainProgram',
      'getSubProgram',
      'inactiveStudent',
      'inactiveStudentData',
      'getStudentStatus'
    ]),
    async fetchData() {
      this.loading = true
      await this.getStudentStatus()
      await this.getMainProgram().then(response => {
        this.main_programs = response.data.map(el => ({
          value: el.acp_id,
          label: el.acp_name_foreign
        }))
      })
      await this.getBranch().then(response => {
        this.branches = response.data.map(el => ({
          value: el.branch_id,
          label: el.branch_name_en
        }))
        this.model.branch_id = this.$store.state.authUser.default_branch_id
      })
      this.loading = false
    },
    onMainProgramChange() {
      this.model.acps_id = []
      this.fetching = true
      if (this.model.acp_id.length <= 0) {
        this.fetching = false
        return
      }
      this.getSubProgram({
        filter: {
          acp_id: this.model.acp_id
        }
      })
        .then(response => {
          this.sub_programs = response.data.map(el => ({
            value: el.acps_id,
            label: el.acps_name_foreign
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.fetching = false
        })
    },
    preview() {
      this.waiting = true
      this.src = ''
      this.activeView = 'preview'
      this.errors = new Errors()
      this.inactiveStudent(this.model)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    loadResults() {
      this.loadingResult = true
      this.activeView = 'result'
      this.errors = new Errors()
      this.records = []
      this.inactiveStudentData(this.model)
        .then(response => {
          this.records = response.records || []
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.loadingResult = false
        })
    },
    getTermName(record) {
      return (
        record.ac_term_name ||
        record.academic_term ||
        record.term_name ||
        this.$t('inactiveStudent.notAvailable')
      )
    },
    getSubProgramName(record) {
      return (
        record.acps_name_foreign ||
        record.acps_name ||
        record.sub_program_name ||
        this.$t('inactiveStudent.notAvailable')
      )
    },
    getMainProgramName(record) {
      const mainProgramFromRecord =
        record.acp_name_foreign ||
        record.acp_name ||
        record.main_program_name

      if (mainProgramFromRecord) {
        return mainProgramFromRecord
      }

      if (this.model.acp_id.length === 1) {
        const selected = this.main_programs.find(p => p.value === this.model.acp_id[0])
        if (selected) {
          return selected.label
        }
      }

      return this.$t('inactiveStudent.notAvailable')
    },
    dateRangeChange(dateRange) {
      this.$store.commit(
        'reportAcademic/SET_DATE_FROM',
        dateRange[0].format('DD-MM-YYYY')
      )
      this.$store.commit(
        'reportAcademic/SET_DATE_TO',
        dateRange[1].format('DD-MM-YYYY')
      )
    }
  }
}
</script>
<style scoped>
.criteria-panel {
  border: 1px solid #dbe2ea;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.criteria-header {
  padding: 10px 14px;
  background: #f4f7fb;
  border-bottom: 1px solid #e6ebf1;
  font-size: 14px;
  font-weight: 700;
  color: #3f5573;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.criteria-title {
  vertical-align: middle;
}

.criteria-icon {
  margin-right: 8px;
  color: #2f80ed;
}

.criteria-body {
  padding: 14px;
}

.criteria-body label {
  margin-bottom: 6px;
  font-size: 12px;
  text-transform: uppercase;
  font-weight: 700;
  color: #566a85;
}

.form-grid {
  margin-right: -7px;
  margin-left: -7px;
}

.form-control-wrap {
  padding-right: 7px;
  padding-left: 7px;
  margin-bottom: 12px;
}

.action-panel {
  margin-top: 2px;
  padding-top: 10px;
  border-top: 1px solid #eef2f7;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.action-left {
  flex: 1;
  min-width: 240px;
}

.action-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.btn-action {
  min-width: 150px;
  min-height: 38px;
  font-weight: 600;
}

.btn-active {
  box-shadow: inset 0 0 0 1px rgba(15, 103, 193, 0.35);
}

.report-view-radio {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.result-header {
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ec;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.result-header--flat {
  border-bottom-color: #e9eef5;
}

.result-tag {
  font-size: 11px;
  font-weight: 700;
  color: #567197;
  background: #edf2f9;
  border-radius: 4px;
  padding: 2px 7px;
}

.result-count {
  background: #eef6ff;
  color: #0f67c1;
  border: 1px solid #d7e7fb;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
}

.result-content {
  margin-top: 0;
}

.result-fieldset {
  border: 0;
  border-radius: 8px;
  background: #fbfdff;
  padding: 4px 0 0;
}

.table-shell {
  border: 0;
  border-radius: 0;
  overflow: hidden;
  background: transparent;
}

.result-table thead th {
  background: #f3f6fb;
  color: #4c6486;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-bottom: 1px solid #e7edf5;
  border-top: 0;
  border-left: 0;
  border-right: 0;
}

.result-table tbody td {
  color: #4f6075;
  font-size: 12px;
  vertical-align: middle;
  border-top: 0;
  border-left: 0;
  border-right: 0;
  border-bottom: 1px solid #eef3f8;
}

.result-table tbody tr:hover td {
  background: #f8fbff;
}

.empty-result {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8da0b8;
  text-align: center;
}

.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  background: #f3f6fb;
  border: 1px solid #e5ebf4;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin-bottom: 12px;
}

.empty-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.empty-subtitle {
  font-size: 12px;
}

@media (max-width: 991.98px) {
  .inactive-student-report .page-header {
    font-size: 22px;
  }

  .result-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .result-count {
    align-self: flex-start;
  }

  .action-panel {
    flex-direction: column;
    align-items: stretch;
  }

  .action-left,
  .action-right {
    width: 100%;
  }

  .action-right {
    justify-content: flex-start;
  }
}

@media (max-width: 767.98px) {
  .criteria-body {
    padding: 12px;
  }

  .criteria-header {
    padding: 9px 12px;
    font-size: 13px;
  }

  .form-control-wrap {
    margin-bottom: 10px;
  }

  .btn-action {
    width: 100%;
    min-height: 42px;
    margin-right: 0 !important;
    margin-bottom: 8px;
  }

  .action-right {
    flex-direction: column;
    align-items: stretch;
  }

  .result-tag {
    margin-left: 8px !important;
  }

  .table-responsive table {
    font-size: 12px;
    min-width: 740px;
  }

  .table-responsive th,
  .table-responsive td {
    white-space: nowrap;
    vertical-align: middle;
  }
}
</style>
